import { useAuthStore } from '@client/stores/AuthStore';
import type { AuthStoreOrg } from '@client/stores/AuthStore/types';
import type { Role } from '@shared/constants';

/**
 * Gate for detail-route `beforeLoad` data prefetches (incident/complaint details).
 *
 * Mirrors `shouldPrefetchHomeDashboardCounts`: only prefetch when the request can
 * actually be authorised (valid token), the hydrated org matches the URL's orgId
 * (an org-switch in flight would prefetch against the wrong tenant context), and
 * the user has one of the roles accepted by the route.
 *
 * `requiredModule` / `allowedRoles` mirror the render-time
 * `RouteAccessGuard` dimensions (`staticData.modules` / `staticData.roles`):
 * `beforeLoad` runs before that guard, so without these checks a deep link by a
 * user the guard would bounce to NotFound fires speculative requests the server
 * denies.
 */
export function shouldPrefetchDetailRouteData({
    hasValidToken,
    userRoles,
    allowedRoles,
    hydratedOrg,
    routeOrgId,
    requiredModule,
}: {
    hasValidToken: boolean;
    userRoles: readonly Role[] | undefined;
    allowedRoles: readonly Role[];
    hydratedOrg: AuthStoreOrg | undefined;
    routeOrgId: bigint;
    requiredModule: AuthStoreOrg['enabledModules'][number];
}): boolean {
    return (
        hasValidToken &&
        !!userRoles?.some((role) => allowedRoles.includes(role)) &&
        hydratedOrg?.id === routeOrgId &&
        hydratedOrg.enabledModules.includes(requiredModule)
    );
}

/**
 * How long a deferred detail-route prefetch waits for the session to restore before giving
 * up. Auth0 token exchange runs ~0.2–1.3 s in production; anything past this window means
 * the user is stuck on the auth gate and the render-time `useQuery` owns the fetch anyway.
 */
export const DETAIL_ROUTE_PREFETCH_AUTH_WAIT_MS = 60_000;

/**
 * Run a detail-route prefetch now if `isAuthorised()` passes, otherwise the moment it starts
 * passing.
 *
 * The dominant cold entry to a detail route is an email deep link: `beforeLoad` runs before
 * `RequireAuth` restores the expired session, so a plainly-guarded prefetch is skipped and
 * the LCP-critical record fetch cannot start until the tab component mounts — serialising
 * auth and data. Waiting on the auth store overlaps the record fetch with the auth gate's
 * final render instead. One-shot: fires at most once, and unsubscribes after
 * `DETAIL_ROUTE_PREFETCH_AUTH_WAIT_MS` if the session never restores. A late fire is safe —
 * `isAuthorised` re-checks the hydrated org against the route's orgId, and the prefetch
 * only warms the query cache.
 *
 * Deferral is for real navigations only. Callers must pass `defer: false` for hover-intent
 * preloads (`beforeLoad`'s `preload` flag): each hovered row would otherwise queue its own
 * deferral during the token-refresh window and the next `setAuthToken` would fire them all
 * as one burst of speculative fetches. Pass the match's `abortController.signal` so leaving
 * or cancelling the navigation tears the deferral down.
 */
export function runDetailRoutePrefetchWhenAuthorised(
    isAuthorised: () => boolean,
    prefetch: () => void,
    { defer = true, signal }: { defer?: boolean; signal?: AbortSignal } = {},
): void {
    if (isAuthorised()) {
        prefetch();
        return;
    }
    if (!defer || signal?.aborted) {
        return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unsubscribe = (): void => {};
    const onAbort = (): void => settle(false);
    const settle = (authorised: boolean): void => {
        if (settled) {
            return;
        }
        settled = true;
        if (timer !== undefined) {
            clearTimeout(timer);
        }
        unsubscribe();
        signal?.removeEventListener('abort', onAbort);
        if (authorised) {
            prefetch();
        }
    };

    unsubscribe = useAuthStore.subscribe(() => {
        // Never throw out of a zustand listener — a throwing listener aborts the store's
        // notification loop, starving every subscriber registered after this one.
        try {
            if (isAuthorised()) {
                settle(true);
            }
        } catch {
            settle(false);
        }
    });
    timer = setTimeout(() => settle(false), DETAIL_ROUTE_PREFETCH_AUTH_WAIT_MS);
    signal?.addEventListener('abort', onAbort, { once: true });
}

/**
 * Let the requests a `beforeLoad` prefetch just queued leave before the page renders.
 *
 * tRPC's batch link sends a batch from a zero-delay timer, but on a navigation whose route
 * code is already loaded the router commits and React renders the new page in the same task
 * (router stores are read through `useSyncExternalStore`, so that render blocks even inside
 * `startTransition`). The page's mount-time queries then join the pending batch and the
 * critical request only leaves after the render. Resolving on a later zero-delay timer lets
 * the batch timer, queued first, send the requests (same-delay timers run in FIFO order);
 * the router then commits while the response is in flight.
 *
 * Must stay a `setTimeout(…, 0)`: a microtask runs before the batch timer, and a
 * MessageChannel, `scheduler.yield()` or animation frame is not ordered after it. Return it
 * only for `cause === 'enter'`, so preloads and navigations that keep this route matched stay
 * synchronous. The router decides `'stay'` by route ID, not by params, so tab switches, search
 * changes and moving between two records of the same route are all `'stay'`: a record-to-record
 * navigation still prefetches here, but its request leaves after the render. `beforeLoad` is
 * not given the previous match, so it cannot tell those cases apart.
 */
export function yieldToPrefetchBatch(): Promise<undefined> {
    return new Promise((resolve) => setTimeout(() => resolve(undefined), 0));
}
